import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getResearchPortfolio } from '../api/api';
import SEO from '../components/SEO';
import { getSEOData } from '../utils/seoData';
import { PROBLEMS, CLINICAL_DISCLAIMER } from '../data/ewm';
import './Home.css';
import './Narrative.css';

// Headline proof figures — identical to the investor documents (Doc 1, slide 2).
const HOME_PROOF = [
  { value: '1.0', label: 'loop accuracy — hash-level round-trip on a real VLA' },
  { value: '100%', label: 'word retention — ordered OCR reconstruction' },
  { value: '32,768', label: 'measurement sites per state, content-addressed' },
  { value: '0', label: 'model changes — no retraining, no fine-tuning, no new weights' },
];

function Home() {
  const [research, setResearch] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const researchData = await getResearchPortfolio();
        setResearch(researchData.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="home">
      <SEO {...getSEOData('home')} />
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-background"></div>
        <div className="container hero-container">
          <div className="hero-content">
            <span className="hero-eyebrow">Emerging World Models</span>
            <h1 className="hero-title">
              Emerging World Models —{' '}
              <span className="gradient-text">a memory side-car beside the model you run</span>
            </h1>
            <p className="hero-subtitle">
              World models are one of the most active directions in AI research. EWM is the part we
              build: the memory.
            </p>
            <p className="hero-description">
              Emerging World Models (EWM) attaches to the model you already run as a side-car — never as
              a replacement. The host keeps its weights, tokenizer, prompts and APIs, and gains a
              structural, content-addressed memory that is{' '}
              <strong>measured, verifiable and portable across models</strong>. Nothing inside the
              model is retrained, fine-tuned or replaced. It runs on-premise, inside your own boundary.
            </p>
            <div className="hero-buttons">
              <Link to="/technology" className="btn btn-primary">How It Works</Link>
              <Link to="/evidence" className="btn btn-secondary">See the Evidence</Link>
            </div>
          </div>
        </div>
      </section>

      {/* What a side-car actually is */}
      <section className="np-section">
        <div className="container">
          <h2 className="np-h2">What a side-car is, in plain words</h2>
          <p className="np-lead">
            This word runs through the whole site, and it is not one everybody meets in their own line
            of work. A side-car is a design pattern — the name people who build software give to a
            reusable way of doing something. This one comes from ordinary life, and it needs no
            technical background to explain.
          </p>

          <div className="np-grid np-grid--2">
            <div className="np-card">
              <h3 className="np-h3">Start with the motorbike</h3>
              <p className="np-body">
                A side-car is the extra seat bolted to the side of a motorbike. The bike is not taken
                apart to fit it: the engine, the wheels and the handlebars stay exactly as they were.
                The side-car simply travels alongside and adds something the bike never had — room for
                a passenger.
              </p>
            </div>
            <div className="np-card">
              <h3 className="np-h3">The same idea sits on things you already own</h3>
              <p className="np-body">
                A phone case with a battery in it gives you days of charge without opening the phone.
                A rack on the back of a car carries bikes without turning it into a different car. An
                external drive gives a laptop more space without taking the laptop apart. In every
                case the main thing stays as it is, and something is attached beside it to add what
                was missing.
              </p>
            </div>
          </div>

          <h3 className="np-h3" style={{ marginTop: '34px' }}>
            Why people build things this way
          </h3>
          <ul className="np-list">
            <li>Nothing that already works gets opened up, so there is no risk of breaking it.</li>
            <li>
              The add-on can be fitted, updated or removed on its own. The main thing never changes.
            </li>
            <li>Each part can be looked after and repaired separately.</li>
            <li>
              It is far quicker and cheaper than rebuilding the main thing to include the new ability.
            </li>
          </ul>

          <h3 className="np-h3" style={{ marginTop: '34px' }}>
            What it costs you
          </h3>
          <ul className="np-list">
            <li>There is now one more thing to fit, watch over, update and keep secure.</li>
            <li>
              Passing information back and forth between the two takes a little extra time and effort.
            </li>
            <li>
              An add-on can only help with what the main thing lets it see. Whatever the main thing
              keeps to itself, the add-on cannot use.
            </li>
            <li>If something goes wrong, there are two parts to check instead of one.</li>
          </ul>

          <p className="np-body" style={{ marginTop: '26px' }}>
            Software is full of this move without ever using the name: a small helper attached to a
            large program to handle logging, security checks or monitoring, so the large program never
            has to be modified for them. It is the same idea as the battery case.
          </p>

          <div className="np-caveat">
            <h3 className="np-h3">Now put your AI model where the motorbike is</h3>
            <p className="np-body">
              Our side-car is bolted on beside the model you already use, and its single job is to
              remember. It listens to the signals the model produces while it works — its internal
              encodings, in technical terms — keeps a record of them outside the model, and can hand
              similar signals back when the model needs them. The model itself is not opened up,
              retrained or replaced; it keeps its weights, its tokenizer and its usual behaviour. The
              same honest limit applies here as anywhere else: we can only work with the signals the
              model makes available to us.
            </p>
          </div>
        </div>
      </section>

      {/* Where memory and context actually sit */}
      <section className="np-section np-section--alt">
        <div className="container">
          <h2 className="np-h2">Context and memory are two different jobs</h2>
          <p className="np-lead">
            The two words are used interchangeably in everyday speech, which is where most confusion
            about AI memory begins. They are related, their purposes overlap, and they are not the
            same thing.
          </p>

          <div className="np-grid np-grid--2">
            <div className="np-card">
              <h3 className="np-h3">Context — the working set</h3>
              <p className="np-body">
                Everything the model can see at the moment it answers: instructions, the conversation
                so far, retrieved passages, tool output. A hosted model call is effectively
                independent of the one before it, so whatever the model conditions on has to travel in
                the request. Context is re-sent every turn, bounded by the window, and gone once the
                call returns.
              </p>
            </div>
            <div className="np-card">
              <h3 className="np-h3">Memory — the durable state</h3>
              <p className="np-body">
                What a system keeps after the call is over and can find again on purpose: decisions,
                results, history, structure. It is not re-sent automatically and it does not expire
                with the call. Memory lives outside the model — in a store, an index, a database — and
                retrieval is the pipe that carries things from there into the context.
              </p>
            </div>
          </div>

          <div className="np-table-wrap" style={{ marginTop: '30px' }}>
            <table className="np-table">
              <thead>
                <tr>
                  <th style={{ width: '170px' }}>&nbsp;</th>
                  <th>Context</th>
                  <th>Memory</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>What it is</strong></td>
                  <td>The working set visible in a single call.</td>
                  <td>State that survives between calls and can be addressed later.</td>
                </tr>
                <tr>
                  <td><strong>Lifetime</strong></td>
                  <td>The length of the call.</td>
                  <td>Until something explicitly removes it.</td>
                </tr>
                <tr>
                  <td><strong>What limits it</strong></td>
                  <td>The context window.</td>
                  <td>Whatever your storage can hold.</td>
                </tr>
                <tr>
                  <td><strong>Cost behaviour</strong></td>
                  <td>Re-sent, and re-priced, on every turn.</td>
                  <td>Read when it is needed.</td>
                </tr>
                <tr>
                  <td><strong>Structure</strong></td>
                  <td>Text and vectors, in the order you assembled them.</td>
                  <td>Whatever the memory design imposes — often nothing.</td>
                </tr>
                <tr>
                  <td><strong>How you check it</strong></td>
                  <td>You can inspect what you sent.</td>
                  <td>Only if the store itself is checkable.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="np-h3" style={{ marginTop: '34px' }}>Why the two get conflated</h3>
          <p className="np-body">
            Because in practice everything a model appears to remember ends up inside the context
            anyway. Context is the channel the model reads; memory is the place information lives
            between calls. Retrieval sits between them: memory → retrieval → context. That is why a
            system can have a very large window and still have no memory, and why adding retrieval
            does not by itself produce a memory you can audit.
          </p>

          <h3 className="np-h3" style={{ marginTop: '34px' }}>Where systems usually intervene</h3>
          <ul className="np-list">
            <li>
              <strong>A larger window.</strong> More context per call; the state still has to be
              re-sent, and cost grows with it.
            </li>
            <li>
              <strong>Summarisation.</strong> Compresses what cannot be re-sent; structure — counts,
              overlaps, what is new — is what gets dropped in the process.
            </li>
            <li>
              <strong>Retrieval.</strong> Gives the state an address so it can be found again; on its
              own it ranks by similarity and guarantees no coverage.
            </li>
            <li>
              <strong>Fine-tuning.</strong> Moves information into the weights, where it is no longer
              separately addressable — and updating it means training again.
            </li>
          </ul>

          <div className="np-caveat">
            <h3 className="np-h3">Where the side-car sits</h3>
            <p className="np-body">
              On the memory side, not the context side. The model still needs its window, and what is
              in that window still matters. What the side-car adds is a home for the durable state
              outside the model, with a structure that can be checked: what is retained, what departed
              and what is new.
            </p>
          </div>
        </div>
      </section>

      {/* Where EWM sits in the wider world-model movement */}
      <section className="np-section">
        <div className="container">
          <h2 className="np-h2 np-h2--center">Where EWM sits in the world-model movement</h2>
          <p className="np-lead np-lead--center">
            World models are a bet much of the field is making — that a system should carry an internal,
            predictive model of its world, not only a larger next-token predictor. Latent predictive
            architectures such as JEPA, learned simulators and generative video models are all part of
            that programme, and it is being advanced by far more groups than ours. We do not claim the
            thesis. We contribute one part of it: the memory.
          </p>
          <div className="np-grid np-grid--3">
            <div className="np-card">
              <h3 className="np-h3">A shared programme, not a contested one</h3>
              <p className="np-body">
                We treat the wider world-model work as the ground EWM grows in. Our papers, our
                vocabulary and our guarantees are meant to sit alongside it — a layer other groups can
                use, including teams building predictive encoders and latent state models.
              </p>
            </div>
            <div className="np-card">
              <h3 className="np-h3">We add to your model, we do not replace it</h3>
              <p className="np-body">
                The side-car reads the host&rsquo;s own internal encodings, keeps state outside the model
                and hands encodings back. Weights, tokenizer, prompts and APIs are untouched — adopting
                the memory never means retiring the model.
              </p>
            </div>
            <div className="np-card">
              <h3 className="np-h3">Model-agnostic on purpose</h3>
              <p className="np-body">
                Because EWM works at the measurement level, one lattice spans model families and
                modalities: a JEPA-style predictive encoder, a vision stack, an OCR model or a driving
                policy. The memory travels; the model stays where it is.
              </p>
            </div>
          </div>
          <div className="np-caveat">
            <h3 className="np-h3">What we do claim</h3>
            <p className="np-body">
              That a memory layer beside a model can be measured, verified and carried across models —
              with no change to the model itself. That is a deliberately narrow claim, and the evidence
              for it is published in full.
            </p>
          </div>
        </div>
      </section>

      {/* The problem */}
      <section className="np-section np-section--alt">
        <div className="container">
          <h2 className="np-h2 np-h2--center">
            The missing piece is memory, not intelligence
          </h2>
          <p className="np-lead np-lead--center">
            Today&rsquo;s models reason impressively, and the mechanisms we use to give them context
            work: a window re-sent each turn, summaries, retrieved chunks. What they are not is a memory
            you can measure. State between calls stays bounded, lossy and unstructured — and that is the
            gap a side-car closes without touching the model.
          </p>
          <div className="np-grid np-grid--3">
            {PROBLEMS.map((p) => (
              <div key={p.id} className="np-card">
                <span className="np-card-num">{p.id}</span>
                <h3 className="np-h3">{p.title}</h3>
                <p className="np-body">{p.body}</p>
              </div>
            ))}
          </div>
          <div className="np-caveat">
            <h3 className="np-h3">How the side-car relates to all three</h3>
            <p className="np-body">
              It does not remove the window, and it does not replace retrieval or summaries. It adds a
              structural state beside them: the model keeps reasoning exactly as it does today, and
              gains a record of what it retained, what departed and what is new.
            </p>
          </div>
          <div className="np-cta-row np-cta-row--center">
            <Link to="/technology" className="np-btn np-btn--primary">
              See how the side-car works
            </Link>
          </div>
        </div>
      </section>

      {/* Measured proof */}
      <section className="np-section">
        <div className="container">
          <h2 className="np-h2 np-h2--center">Measured on real models, with the caveats published</h2>
          <p className="np-lead np-lead--center">
            Every figure below comes from a run in our own repositories — real model encodings, real
            documents, on our own machines. Where a number has a limit, the limit is printed beside it.
          </p>
          <div className="np-stats">
            {HOME_PROOF.map((s) => (
              <div key={s.label} className="np-stat">
                <span className="np-stat-value">{s.value}</span>
                <span className="np-stat-label">{s.label}</span>
              </div>
            ))}
          </div>
          <div className="np-cta-row np-cta-row--center">
            <Link to="/evidence" className="np-btn np-btn--primary">
              All six measured scenarios
            </Link>
            <Link to="/publications" className="np-btn np-btn--secondary">
              Published record
            </Link>
          </div>
          <div className="np-disclaimer" style={{ marginTop: '32px' }}>
            {CLINICAL_DISCLAIMER}
          </div>
        </div>
      </section>

      {/* Research & Innovation Section */}
      {research.length > 0 && (
        <section className="section research-section">
          <div className="container">
            <h2 className="section-title">AI Research & Innovation — Products in Progress</h2>
            <p className="section-subtitle">
              Five products in progress, all built on the same measurement lattice as the side-car —
              language models, world models, vision, document intelligence and vision-language-action
              autonomy.
            </p>

            <div className="research-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '20px',
              marginTop: '40px'
            }}>
              {research.map((item) => (
                <div key={item.id} className="research-card" style={{
                  background: 'var(--white, #fff)',
                  borderRadius: '14px',
                  padding: '22px 22px 18px',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
                  border: '1px solid rgba(25, 118, 210, 0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '1.8rem' }}>{item.icon}</span>
                    <span style={{
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      padding: '4px 10px',
                      borderRadius: '999px',
                      background: item.status === 'Implemented' ? '#E8F5E9' :
                                  item.status === 'Near-term' ? '#FFF3E0' :
                                  item.status === 'Medium-term' ? '#E3F2FD' : '#F3E5F5',
                      color: item.status === 'Implemented' ? '#2E7D32' :
                             item.status === 'Near-term' ? '#E65100' :
                             item.status === 'Medium-term' ? '#1565C0' : '#6A1B9A'
                    }}>
                      {item.status}
                    </span>
                  </div>
                  <h3 style={{ margin: '4px 0 0', fontSize: '1.15rem', fontWeight: 800 }}>{item.name}</h3>
                  <div style={{ fontSize: '0.78rem', color: '#666', fontWeight: 600 }}>{item.category}</div>
                  <p style={{ margin: '6px 0 0', fontSize: '0.92rem', color: '#444', lineHeight: 1.5 }}>
                    {item.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="text-center" style={{ marginTop: '40px' }}>
              <Link to="/contact" className="btn btn-primary">Partner with Our Research Team</Link>
            </div>
          </div>
        </section>
      )}

      {/* Why Choose Us Section */}
      <section className="section why-choose-us">
        <div className="container">
          <h2 className="section-title">What the side-car gives the model you already run</h2>
          <p className="section-subtitle">
            Six properties of the memory layer, each one traceable to a run rather than asserted.
          </p>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🔬</div>
              <h3>A Structure, Not a Summary</h3>
              <p>D / R / N per commit — departed, retained, novel — with a conservation balance. A precise answer to “what changed?”</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🤖</div>
              <h3>The Model Is Untouched</h3>
              <p>Zero model changes — no retraining, no fine-tuning, no new weights. The host keeps its weights, tokenizer and APIs.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🛡️</div>
              <h3>Auditable by Construction</h3>
              <p>Content-addressed, immutable commits give an append-only record — exactly what an auditor asks for.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🌐</div>
              <h3>Portable Across Models</h3>
              <p>Measurement-level and model-agnostic: the same lattice spans hosts and modalities, so memory is not trapped in one checkpoint.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔒</div>
              <h3>On-Premise &amp; Air-Gapped</h3>
              <p>Deployed inside your own boundary, including air-gapped environments with zero outbound calls. The state store sits in storage you control.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📈</div>
              <h3>Proven on Real Models</h3>
              <p>Six measured scenarios across a vision-language-action stack, OCR, vision grids and a document platform.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Bring your own model — we bring the memory</h2>
            <p>Run the side-car loop against your own model as a design partner, pilot it on a real workload, or engage our team on a custom build. Your weights, tokenizer and APIs stay exactly as they are.</p>
            <Link to="/partners" className="btn btn-large">Become a Design Partner</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
